import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { addDoc, collection, doc, getDoc, getDocs, limit, orderBy, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import SmsService from '../../../services/SmsService';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';

interface PortvilleApplication {
  id: string;
  uid: string;
  name: string;
  email: string;
  phoneNumber: string;
  organization: string;
  userLevel: number;
  additionalRequest: string;
  applyCode?: string;
  documents?: {
    identification: string;
    employment: string;
    other?: string;
  };
  status: 'pending' | 'checked' | 'approved' | 'rejected' | 'consumed';
  createdAt: any;
  updatedAt: any;
  approvedAt?: any;
  rejectedAt?: any;
  rejectionReason?: string;
}

interface PortvilleApplicationAdminMemo {
  applicationId?: string;
  memo?: string;
  createdAt?: any;
  createdByUid?: string;
  createdByEmail?: string | null;
  updatedAt?: any;
  updatedByUid?: string;
  updatedByEmail?: string | null;
}

interface PortvilleApplicationSmsLog {
  type: 'approved_notice' | 'rejected_notice';
  isResend: boolean;
  phoneNumber: string;
  sentAt: any;
  success: boolean;
  reason?: string;
}

const toDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatYyyyMmDdDots = (date: Date): string => {
  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd}`;
};

const PortvilleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, userLevel, permissions, loading: authLoading } = useAdminAuth();
  const [application, setApplication] = useState<PortvilleApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [waitingPosition, setWaitingPosition] = useState<number | null>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);
  const [adminMemo, setAdminMemo] = useState<PortvilleApplicationAdminMemo | null>(null);
  const [adminMemoText, setAdminMemoText] = useState<string>('');
  const [adminMemoInitialText, setAdminMemoInitialText] = useState<string>('');
  const [adminMemoLoading, setAdminMemoLoading] = useState<boolean>(false);
  const [adminMemoSaving, setAdminMemoSaving] = useState<boolean>(false);
  const [adminMemoError, setAdminMemoError] = useState<string | null>(null);
  const [adminMemoExists, setAdminMemoExists] = useState<boolean>(false);
  const [approvedSmsLogs, setApprovedSmsLogs] = useState<PortvilleApplicationSmsLog[]>([]);
  const [approvedSmsLoading, setApprovedSmsLoading] = useState<boolean>(false);
  const [approvedSmsSending, setApprovedSmsSending] = useState<boolean>(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [selectedApplicationUserLevel, setSelectedApplicationUserLevel] = useState<number>(9);
  const [userLevelSaving, setUserLevelSaving] = useState<boolean>(false);

  // userLevel을 등급명으로 변환하는 헬퍼 함수
  const getUserLevelLabel = (level: number): string => {
    const levelMap: { [key: number]: string } = {
      1: '최고 관리자',
      2: '일반 관리자',
      3: '공사 관계자',
      4: '센터 관계자',
      5: '입주업체',
      6: '입주협력업체',
      7: '공공기관',
      8: '항만근로자',
      9: '일반 사용자'
    };
    return levelMap[level] || '알 수 없음';
  };

  const hasPortvilleAdminAccess = Boolean(user && ((userLevel !== null && userLevel <= 2) || permissions?.portville));

  const getApprovedNoticeMessage = (app: PortvilleApplication) => {
    const approvedAtDate = toDate(app.approvedAt) ?? new Date();
    const deadlineText = formatYyyyMmDdDots(addDays(approvedAtDate, 7));
    return `[포트빌] ${app.name} 님. 입주 순번이 도래했습니다. ${deadlineText}까지 부산항신항시설관리센터 홈페이지에 로그인 후 확인해 주세요. 기한 초과시 대기가 취소되어 다음 순번으로 넘어가게 됩니다.`;
  };

  const fetchApprovedSmsLogs = async (applicationId: string) => {
    try {
      setApprovedSmsLoading(true);
      const logsRef = collection(db, 'portville_applications', applicationId, 'sms_logs');
      const logsQuery = query(
        logsRef,
        orderBy('sentAt', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(logsQuery);
      const logs = snapshot.docs
        .map(docSnap => docSnap.data() as PortvilleApplicationSmsLog)
        .filter(log => log.type === 'approved_notice');
      setApprovedSmsLogs(logs);
    } catch (err) {
      console.error('입주 안내 문자 로그 로딩 오류:', err);
      setApprovedSmsLogs([]);
    } finally {
      setApprovedSmsLoading(false);
    }
  };

  const writeApprovedSmsLog = async (applicationId: string, log: PortvilleApplicationSmsLog) => {
    const logsRef = collection(db, 'portville_applications', applicationId, 'sms_logs');
    await addDoc(logsRef, log);
  };

  useEffect(() => {
    fetchApplicationDetail();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (authLoading) return;
    if (!hasPortvilleAdminAccess) return;

    const fetchAdminMemo = async () => {
      try {
        setAdminMemoLoading(true);
        setAdminMemoError(null);

        const memoRef = doc(db, 'portville_application_admin_memos', id);
        const memoSnap = await getDoc(memoRef);

        if (memoSnap.exists()) {
          const memoData = memoSnap.data() as PortvilleApplicationAdminMemo;
          const nextMemoText = memoData.memo ?? '';
          setAdminMemo(memoData);
          setAdminMemoText(nextMemoText);
          setAdminMemoInitialText(nextMemoText);
          setAdminMemoExists(true);
        } else {
          setAdminMemo(null);
          setAdminMemoText('');
          setAdminMemoInitialText('');
          setAdminMemoExists(false);
        }
      } catch (err) {
        console.error('관리자 메모 로딩 오류:', err);
        setAdminMemoError('관리자 메모를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setAdminMemoLoading(false);
      }
    };

    fetchAdminMemo();
  }, [id, authLoading, hasPortvilleAdminAccess]);

  const fetchWaitingPosition = async (app: PortvilleApplication) => {
    if (!app?.createdAt) {
      setWaitingPosition(null);
      return;
    }

    if (app.status === 'approved' || app.status === 'rejected' || app.status === 'consumed') {
      setWaitingPosition(null);
      return;
    }

    try {
      const waitingQuery = query(
        collection(db, 'portville_applications'),
        where('status', 'in', ['pending', 'checked']),
        where('createdAt', '<', app.createdAt),
        orderBy('createdAt', 'asc')
      );
      const waitingSnapshot = await getDocs(waitingQuery);
      setWaitingPosition(waitingSnapshot.size + 1);
    } catch (err) {
      console.error('대기 순번 계산 오류:', err);
      setWaitingPosition(null);
    }
  };

  const fetchApplicationDetail = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const docRef = doc(db, 'portville_applications', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const app = {
          id: docSnap.id,
          ...docSnap.data()
        } as PortvilleApplication;
        setApplication(app);
        setSelectedApplicationUserLevel(app.userLevel);
        await fetchWaitingPosition(app);
        await fetchApprovedSmsLogs(docSnap.id);
      } else {
        setError('신청서를 찾을 수 없습니다.');
      }
    } catch (err) {
      console.error('신청서 상세 정보 로딩 오류:', err);
      setError('신청서 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (url: string, type: string) => {
    try {
      setDownloading(type);
      const storageRef = ref(storage, url);
      const downloadUrl = await getDownloadURL(storageRef);
      
      // 파일 확장자 추출
      const extension = url.split('.').pop() || 'pdf';
      
      // 파일 다운로드
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      // 다운로드 링크 생성 및 클릭
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${application?.name}_${type}.${extension}`; // 원본 확장자 사용
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('파일 다운로드 오류:', err);
      alert('파일 다운로드 중 오류가 발생했습니다.');
    } finally {
      setDownloading(null);
    }
  };

  const handleApplicationUserLevelSave = async () => {
    if (!id || !application || userLevelSaving) return;

    if (application.status !== 'pending') {
      alert('대기중인 신청서만 회원등급을 수정할 수 있습니다.');
      return;
    }

    if (!Number.isInteger(selectedApplicationUserLevel) || selectedApplicationUserLevel < 3 || selectedApplicationUserLevel > 9) {
      alert('유효한 회원등급을 선택해주세요.');
      return;
    }

    try {
      setUserLevelSaving(true);
      const applicationRef = doc(db, 'portville_applications', id);
      const latestSnapshot = await getDoc(applicationRef);

      if (!latestSnapshot.exists()) {
        alert('신청서를 찾을 수 없습니다.');
        return;
      }

      const latestApplication = latestSnapshot.data() as PortvilleApplication;
      if (latestApplication.status !== 'pending') {
        alert('신청서 상태가 변경되어 회원등급을 수정할 수 없습니다.');
        await fetchApplicationDetail();
        return;
      }

      const updatedAt = new Date();
      await updateDoc(applicationRef, {
        userLevel: selectedApplicationUserLevel,
        updatedAt,
      });

      setApplication(current => current ? {
        ...current,
        userLevel: selectedApplicationUserLevel,
        updatedAt,
      } : current);
      alert('신청서 회원등급이 수정되었습니다.');
    } catch (err) {
      console.error('신청서 회원등급 수정 오류:', err);
      alert('신청서 회원등급 수정 중 오류가 발생했습니다.');
    } finally {
      setUserLevelSaving(false);
    }
  };

  const handleStatusUpdate = async (newStatus: 'pending' | 'checked' | 'approved' | 'rejected', reason?: string) => {
    if (!id || !application) return;

    if (newStatus === 'rejected' && !reason?.trim()) {
      alert('취소 사유를 입력해주세요.');
      return;
    }

    // 중복 클릭 방지: 마지막 처리 후 3초 이내에는 재처리 불가
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTime;
    if (timeSinceLastUpdate < 3000) {
      alert(`잠시 후 다시 시도해주세요. (${Math.ceil((3000 - timeSinceLastUpdate) / 1000)}초 후 가능)`);
      return;
    }

    try {
      // checked 상태로 변경할 때만 userLevel 비교
      if (newStatus === 'checked') {
        const userDoc = await getDoc(doc(db, 'user_profiles', application.uid));
        const userData = userDoc.data();

        if (userData && userData.userLevel !== application.userLevel) {
          const currentLevelLabel = getUserLevelLabel(userData.userLevel);
          const applicationLevelLabel = getUserLevelLabel(application.userLevel);

          const confirmed = window.confirm(
            `회원등급이 다릅니다.\n\n` +
            `현재 등급: ${currentLevelLabel} (${userData.userLevel})\n` +
            `신청서 등급: ${applicationLevelLabel} (${application.userLevel})\n\n` +
            `검토처리를 진행하면 회원등급이 신청서 등급으로 변경됩니다.\n` +
            `계속 진행하시겠습니까?`
          );

          if (!confirmed) {
            return;
          }

          // userLevel 업데이트
          const userProfileRef = doc(db, 'user_profiles', application.uid);
          await updateDoc(userProfileRef, {
            userLevel: application.userLevel,
            updatedAt: new Date()
          });
        }
      }

	      setUpdating(true);
	      setLastUpdateTime(now); // 처리 시작 시간 기록

	      const docRef = doc(db, 'portville_applications', id);
	      const updatedAt = new Date();
	      const nextUpdate: Record<string, any> = {
	        status: newStatus,
	        updatedAt
	      };
	      const approvedAt = newStatus === 'approved' ? updatedAt : undefined;
	      if (approvedAt) nextUpdate.approvedAt = approvedAt;
	      if (newStatus === 'rejected') {
	        nextUpdate.rejectedAt = updatedAt;
	        nextUpdate.rejectionReason = reason?.trim();
	      }
	      await updateDoc(docRef, nextUpdate);

      // 상태 변경 시 SMS 발송
	      if ((newStatus === 'checked' || newStatus === 'approved' || newStatus === 'rejected') && application.phoneNumber) {
	        try {
	          let smsMessage = '';

	          if (newStatus === 'checked') {
	            smsMessage = `[포트빌] ${application.name} 님 입주 대기 신청이 완료되었습니다. 홈페이지에서 대기 순번을 확인해주세요.`;
	          } else if (newStatus === 'approved') {
	            const approvedAtDate = approvedAt ?? toDate(application.approvedAt) ?? new Date();
	            const deadlineText = formatYyyyMmDdDots(addDays(approvedAtDate, 7));
	            smsMessage = `[포트빌] ${application.name} 님 입주 순번이 도래했습니다. ${deadlineText}까지 홈페이지의 안내를 확인해 주세요.`;
	          } else if (newStatus === 'rejected') {
	            smsMessage = `[포트빌] ${application.name} 님 입주 신청이 취소되었습니다.`;
	          }

	          if (smsMessage) {
	            await SmsService.sendSMS(
	              application.phoneNumber,
	              smsMessage,
	              newStatus === 'approved' ? { subject: '포트빌 입주 안내' } : undefined
	            );
	            console.log('포트빌 상태변경 SMS 발송 완료:', application.phoneNumber);
	          }

          if (newStatus === 'rejected') {
            if (reason?.trim()) {
              await SmsService.sendSMS(application.phoneNumber, `취소 사유: ${reason.trim()}`);
            }

            try {
              await writeApprovedSmsLog(id, {
                type: 'rejected_notice',
                isResend: false,
                phoneNumber: application.phoneNumber,
                sentAt: new Date(),
                success: true,
                reason: reason?.trim(),
              });
            } catch (logError) {
              console.error('취소 안내 문자 로그 저장 실패:', logError);
            }
          }

          if (newStatus === 'approved') {
            try {
              await writeApprovedSmsLog(id, {
                type: 'approved_notice',
                isResend: false,
                phoneNumber: application.phoneNumber,
                sentAt: new Date(),
                success: true,
              });
            } catch (logError) {
              console.error('입주 안내 문자 로그 저장 실패:', logError);
            }
          }
        } catch (smsError) {
          console.error('SMS 발송 실패:', smsError);
          if (newStatus === 'approved') {
            try {
              await writeApprovedSmsLog(id, {
                type: 'approved_notice',
                isResend: false,
                phoneNumber: application.phoneNumber,
                sentAt: new Date(),
                success: false,
              });
            } catch (logError) {
              console.error('입주 안내 문자 실패 로그 저장 실패:', logError);
            }
          }
          if (newStatus === 'rejected') {
            try {
              await writeApprovedSmsLog(id, {
                type: 'rejected_notice',
                isResend: false,
                phoneNumber: application.phoneNumber,
                sentAt: new Date(),
                success: false,
                reason: reason?.trim(),
              });
            } catch (logError) {
              console.error('취소 안내 문자 실패 로그 저장 실패:', logError);
            }
          }
          // SMS 발송 실패는 상태 업데이트를 롤백하지 않음
          // 에러 메시지를 더 명확하게 개선
          const errorMessage = smsError instanceof Error ? smsError.message : 'SMS 발송에 실패했습니다.';
          if (errorMessage.includes('timeout') || errorMessage.includes('타임아웃')) {
            alert('상태는 변경되었으나 SMS 발송 확인에 시간이 걸리고 있습니다. 잠시 후 문자 수신을 확인해주세요.');
          } else {
            alert('상태는 변경되었으나 SMS 발송에 실패했습니다.');
          }
        }
      }

      if (newStatus === 'rejected') {
        setIsCancelModalOpen(false);
        setCancelReason('');
      }

      // 상태 업데이트 후 애플리케이션 정보 다시 로드
      await fetchApplicationDetail();
    } catch (err) {
      console.error('상태 업데이트 오류:', err);
      alert('상태 업데이트 중 오류가 발생했습니다.');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelClick = () => {
    setCancelReason('');
    setIsCancelModalOpen(true);
  };

  const handleCancelClose = () => {
    if (updating) return;
    setCancelReason('');
    setIsCancelModalOpen(false);
  };

  const handleCancelSubmit = () => {
    handleStatusUpdate('rejected', cancelReason);
  };

  const handleResendApprovedNotice = async () => {
    if (!id || !application) return;
    if (application.status !== 'approved') return;
    if (!application.phoneNumber) {
      alert('연락처 정보가 없어 문자를 발송할 수 없습니다.');
      return;
    }

    const confirmed = window.confirm('입주 안내 문자를 재발송하시겠습니까?');
    if (!confirmed) return;

    try {
      setApprovedSmsSending(true);
      const message = getApprovedNoticeMessage(application);
      await SmsService.sendSMS(application.phoneNumber, message, { subject: '포트빌 입주 안내' });
      await writeApprovedSmsLog(id, {
        type: 'approved_notice',
        isResend: true,
        phoneNumber: application.phoneNumber,
        sentAt: new Date(),
        success: true,
      });
      await fetchApprovedSmsLogs(id);
      alert('입주 안내 문자를 발송했습니다.');
    } catch (err) {
      console.error('입주 안내 문자 재발송 실패:', err);
      try {
        await writeApprovedSmsLog(id, {
          type: 'approved_notice',
          isResend: true,
          phoneNumber: application.phoneNumber,
          sentAt: new Date(),
          success: false,
        });
        await fetchApprovedSmsLogs(id);
      } catch (logError) {
        console.error('입주 안내 문자 재발송 실패 로그 저장 실패:', logError);
      }
      alert('입주 안내 문자 발송에 실패했습니다.');
    } finally {
      setApprovedSmsSending(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-3 py-1 text-sm font-semibold rounded-full bg-yellow-100 text-yellow-800">대기중</span>;
      case 'checked':
        return <span className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">검토완료</span>;
      case 'approved':
        return <span className="px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">입주진행</span>;
      case 'rejected':
        return <span className="px-3 py-1 text-sm font-semibold rounded-full bg-red-100 text-red-800">취소됨</span>;
      default:
        return null;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    const date =
      typeof timestamp?.toDate === 'function'
        ? timestamp.toDate()
        : timestamp instanceof Date
          ? timestamp
          : new Date(timestamp);
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const latestApprovedSmsLog = approvedSmsLogs.length > 0 ? approvedSmsLogs[0] : null;

  const handleSaveAdminMemo = async () => {
    if (!id) return;
    if (!user) return;
    if (!hasPortvilleAdminAccess) return;

    try {
      setAdminMemoSaving(true);
      setAdminMemoError(null);

      const memoRef = doc(db, 'portville_application_admin_memos', id);
      const now = new Date();
      const nextMemoText = adminMemoText ?? '';

      if (adminMemoExists) {
        await updateDoc(memoRef, {
          memo: nextMemoText,
          updatedAt: now,
          updatedByUid: user.uid,
          updatedByEmail: user.email ?? null
        });
      } else {
        await setDoc(
          memoRef,
          {
            applicationId: id,
            memo: nextMemoText,
            createdAt: now,
            createdByUid: user.uid,
            createdByEmail: user.email ?? null,
            updatedAt: now,
            updatedByUid: user.uid,
            updatedByEmail: user.email ?? null
          },
          { merge: true }
        );
      }

      setAdminMemo({
        applicationId: id,
        memo: nextMemoText,
        createdAt: adminMemo?.createdAt ?? now,
        createdByUid: adminMemo?.createdByUid ?? user.uid,
        createdByEmail: adminMemo?.createdByEmail ?? (user.email ?? null),
        updatedAt: now,
        updatedByUid: user.uid,
        updatedByEmail: user.email ?? null
      });
      setAdminMemoInitialText(nextMemoText);
      setAdminMemoExists(true);
    } catch (err) {
      console.error('관리자 메모 저장 오류:', err);
      setAdminMemoError('관리자 메모 저장 중 오류가 발생했습니다.');
      alert('관리자 메모 저장 중 오류가 발생했습니다.');
    } finally {
      setAdminMemoSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="text-center text-red-600 p-4">
        <p>{error || '신청서를 찾을 수 없습니다.'}</p>
        <button
          onClick={() => navigate('/admin/portville', { state: { restoreScroll: true } })}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">포트빌 입주신청 상세</h1>
        <button
          onClick={() => navigate('/admin/portville', { state: { restoreScroll: true } })}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
        >
          목록으로
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* 상태 표시 및 변경 버튼 */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <span className="text-lg font-medium text-gray-900">{application.name}</span>
              {getStatusBadge(application.status)}
              {(application.status === 'pending' || application.status === 'checked') && waitingPosition && (
                <span className="px-3 py-1 text-sm font-semibold rounded-full bg-slate-100 text-slate-800">
                  대기 순번 {waitingPosition}번
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleStatusUpdate('pending')}
                disabled={updating || application.status === 'pending'}
                className={`px-3 py-1 text-sm rounded-md ${
                  application.status === 'pending'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                }`}
              >
                대기중
              </button>
              <button
                onClick={() => handleStatusUpdate('checked')}
                disabled={updating || application.status === 'checked' || !application.uid}
                className={`px-3 py-1 text-sm rounded-md ${
                  application.status === 'checked' || !application.uid
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                }`}
                title={!application.uid ? '사용자 정보가 없는 신청서는 검토처리할 수 없습니다.' : ''}
              >
                검토처리
              </button>
              <button
                onClick={() => handleStatusUpdate('approved')}
                disabled={updating || application.status === 'approved'}
                className={`px-3 py-1 text-sm rounded-md ${
                  application.status === 'approved'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-100 text-green-800 hover:bg-green-200'
                }`}
              >
                입주진행
              </button>
              <button
                onClick={handleCancelClick}
                disabled={updating || application.status === 'rejected'}
                className={`px-3 py-1 text-sm rounded-md ${
                  application.status === 'rejected'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-red-100 text-red-800 hover:bg-red-200'
                }`}
              >
                취소
              </button>
            </div>
          </div>
            <div className="mt-2 text-sm text-gray-500">
              신청일시: {formatDate(application.createdAt)}
              <span className="ml-4">
                입주진행 전환(마지막): {application.approvedAt ? formatDate(application.approvedAt) : '기록 없음'}
              </span>
              <span className="ml-4">
                입주 안내 문자(마지막):{' '}
                {latestApprovedSmsLog ? `${formatDate(latestApprovedSmsLog.sentAt)} (${latestApprovedSmsLog.success ? '성공' : '실패'})` : '기록 없음'}
              </span>
              {application.updatedAt && application.updatedAt !== application.createdAt && (
                <span className="ml-4">
                  마지막 수정: {formatDate(application.updatedAt)}
                </span>
              )}
              {application.status === 'rejected' && application.rejectionReason && (
                <span className="ml-4">
                  취소 사유: {application.rejectionReason}
                </span>
              )}
            </div>
          </div>

        {/* 신청자 정보 */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">신청자 정보</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">이메일</p>
              <p className="font-medium">{application.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">연락처</p>
              <p className="font-medium">{application.phoneNumber}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">소속</p>
              <p className="font-medium">{application.organization}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">신청서 회원등급</p>
              {application.status === 'pending' ? (
                <div className="mt-1 flex items-center gap-2">
                  <select
                    value={selectedApplicationUserLevel}
                    onChange={(event) => setSelectedApplicationUserLevel(Number(event.target.value))}
                    disabled={userLevelSaving || updating}
                    aria-label="신청서 회원등급"
                    className="h-10 min-w-44 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    <option value={9}>일반 사용자 (9)</option>
                    <option value={8}>항만근로자 (8)</option>
                    <option value={7}>공공기관 (7)</option>
                    <option value={6}>입주협력업체 (6)</option>
                    <option value={5}>입주업체 (5)</option>
                    <option value={4}>센터 관계자 (4)</option>
                    <option value={3}>공사 관계자 (3)</option>
                  </select>
                  <button
                    type="button"
                    onClick={handleApplicationUserLevelSave}
                    disabled={userLevelSaving || updating || selectedApplicationUserLevel === application.userLevel}
                    className="h-10 px-4 rounded-md bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {userLevelSaving ? '저장 중...' : '저장'}
                  </button>
                </div>
              ) : (
                <p className="font-medium">{getUserLevelLabel(application.userLevel)} ({application.userLevel})</p>
              )}
            </div>
          </div>
        </div>

        {/* 추가 요청사항 */}
	        {application.additionalRequest && (
	          <div className="px-6 py-4 border-b border-gray-200">
	            <h2 className="text-lg font-medium text-gray-900 mb-2">추가 요청사항</h2>
	            <p className="text-gray-700 whitespace-pre-line">{application.additionalRequest}</p>
	          </div>
	        )}

          {/* 입주 안내 문자 (approved 상태에서만) */}
          {hasPortvilleAdminAccess && application.status === 'approved' && (
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-medium text-gray-900">입주 안내 문자</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    최근 발송: {latestApprovedSmsLog ? `${formatDate(latestApprovedSmsLog.sentAt)} (${latestApprovedSmsLog.success ? '성공' : '실패'})` : '기록 없음'}
                  </p>
                </div>
                <button
                  onClick={handleResendApprovedNotice}
                  disabled={approvedSmsSending}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {approvedSmsSending ? '발송 중...' : '입주 안내 문자 재발송'}
                </button>
              </div>

              <div className="mt-4">
                <div className="text-sm font-medium text-gray-700 mb-2">발송 이력(최근 20건)</div>
                {approvedSmsLoading ? (
                  <div className="text-sm text-gray-500">로딩 중...</div>
                ) : approvedSmsLogs.length === 0 ? (
                  <div className="text-sm text-gray-500">기록 없음</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500">
                          <th className="py-2 pr-4 font-medium">발송일시</th>
                          <th className="py-2 pr-4 font-medium">수신번호</th>
                          <th className="py-2 pr-4 font-medium">구분</th>
                          <th className="py-2 pr-4 font-medium">결과</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-800">
                        {approvedSmsLogs.map((log, index) => (
                          <tr key={`${String(log.sentAt)}-${index}`} className="border-t border-gray-100">
                            <td className="py-2 pr-4">{formatDate(log.sentAt)}</td>
                            <td className="py-2 pr-4">{log.phoneNumber}</td>
                            <td className="py-2 pr-4">{log.isResend ? '재발송' : '자동발송'}</td>
                            <td className="py-2 pr-4">{log.success ? '성공' : '실패'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

	        {/* 관리자 메모 (관리자만 UI 노출) */}
	        {hasPortvilleAdminAccess && (
	          <div className="px-6 py-4 border-b border-gray-200">
	            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-lg font-medium text-gray-900">관리자 메모</h2>
                <p className="text-xs text-gray-500">이 메모는 관리자만 확인할 수 있습니다.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setAdminMemoText(adminMemoInitialText)}
                  disabled={adminMemoSaving || adminMemoLoading || adminMemoText === adminMemoInitialText}
                  className="px-3 py-1 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  되돌리기
                </button>
                <button
                  onClick={handleSaveAdminMemo}
                  disabled={adminMemoSaving || adminMemoLoading || adminMemoText === adminMemoInitialText}
                  className="px-3 py-1 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {adminMemoSaving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>

            {adminMemoLoading ? (
              <div className="text-sm text-gray-500">메모 불러오는 중...</div>
            ) : (
              <>
                <textarea
                  value={adminMemoText}
                  onChange={(e) => setAdminMemoText(e.target.value)}
                  placeholder="입주신청 건에 대한 관리자 메모를 입력하세요. (선택사항)"
                  className="w-full h-24 p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {adminMemo?.updatedAt ? (
                      <>
                        마지막 수정: {formatDate(adminMemo.updatedAt)}
                        {adminMemo.updatedByEmail ? ` (${adminMemo.updatedByEmail})` : ''}
                      </>
                    ) : (
                      '아직 저장된 메모가 없습니다.'
                    )}
                  </div>
                  {adminMemoError && <div className="text-xs text-red-600">{adminMemoError}</div>}
                </div>
              </>
            )}
          </div>
        )}

        {/* 제출 서류 */}
        <div className="px-6 py-4">
          <h2 className="text-lg font-medium text-gray-900 mb-4">제출 서류</h2>
          {application.documents ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">신분증 사본</span>
                <button
                  onClick={() => handleDownload(application.documents?.identification || '', 'identification')}
                  disabled={downloading === 'identification' || !application.documents?.identification}
                  className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                >
                  {downloading === 'identification' ? '다운로드 중...' : '다운로드'}
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">재직증명서/사업자등록증</span>
                <button
                  onClick={() => handleDownload(application.documents?.employment || '', 'employment')}
                  disabled={downloading === 'employment' || !application.documents?.employment}
                  className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                >
                  {downloading === 'employment' ? '다운로드 중...' : '다운로드'}
                </button>
              </div>
              {application.documents?.other && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">기타 서류</span>
                  <button
                    onClick={() => handleDownload(application.documents?.other || '', 'other')}
                    disabled={downloading === 'other'}
                    className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                  >
                    {downloading === 'other' ? '다운로드 중...' : '다운로드'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-500">
              제출된 서류가 없습니다.
            </div>
          )}
        </div>
      </div>

      {isCancelModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm p-8 max-w-md w-full outline outline-1 outline-slate-200">
            <h3 className="text-lg font-bold text-[#1e2c49] font-['Pretendard'] mb-6">입주 신청 취소</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">
                  취소 사유 *
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard'] min-h-[120px]"
                  placeholder="입주 신청 취소 사유를 입력해주세요."
                  required
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4">
                <p className="text-sm text-yellow-800 font-['Pretendard']">
                  신청을 취소하면 신청자에게 SMS로 취소 알림과 사유가 발송됩니다.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancelClose}
                  disabled={updating}
                  className="px-6 py-3 text-sm font-medium text-[#6b7684] bg-white border border-[#e5e8eb] rounded-sm hover:bg-[#f8fbff] font-['Pretendard'] transition-colors disabled:opacity-50"
                >
                  닫기
                </button>
                <button
                  type="button"
                  onClick={handleCancelSubmit}
                  disabled={updating}
                  className="px-6 py-3 text-sm font-medium text-white bg-red-600 rounded-sm hover:bg-red-700 disabled:opacity-50 font-['Pretendard'] transition-colors"
                >
                  {updating ? '처리 중...' : '신청 취소'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortvilleDetailPage; 
